import React from 'react';
import { Clock, Copy } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  OnboardingFormData,
  DayOfWeek,
  DAYS_OF_WEEK,
  DayHours,
} from '../../../types/business';
import { cn } from '../../../lib/utils';

interface StepHoursProps {
  formData: OnboardingFormData;
  updateField: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
  error: string | null;
}

const DAY_LABELS: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const PREP_TIME_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
];

export function StepHours({ formData, updateField, error }: StepHoursProps) {
  const updateDayHours = (day: DayOfWeek, field: keyof DayHours, value: string) => {
    const current = formData.hours[day];
    if (!current) return;
    updateField('hours', {
      ...formData.hours,
      [day]: { ...current, [field]: value },
    });
  };

  const toggleDay = (day: DayOfWeek) => {
    const current = formData.hours[day];
    updateField('hours', {
      ...formData.hours,
      [day]: current ? null : { open: '10:00', close: '18:00' },
    });
  };

  const copyToAll = (sourceDay: DayOfWeek) => {
    const sourceHours = formData.hours[sourceDay];
    const newHours = { ...formData.hours };
    DAYS_OF_WEEK.forEach(day => {
      if (day !== sourceDay && formData.hours[day] !== null) {
        newHours[day] = sourceHours ? { ...sourceHours } : null;
      }
    });
    updateField('hours', newHours);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif font-semibold text-heading">
          Hours of operation
        </h2>
        <p className="text-body mt-1">
          Set when your boutique accepts orders. You can change these anytime.
        </p>
      </div>

      {/* Weekly Hours */}
      <div className="space-y-3">
        {DAYS_OF_WEEK.map((day) => {
          const hours = formData.hours[day];
          const isOpen = hours !== null;

          return (
            <div
              key={day}
              className={cn(
                'flex items-center gap-3 p-3 rounded-xl border transition-colors',
                isOpen ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50/50'
              )}
            >
              {/* Toggle */}
              <button
                type="button"
                onClick={() => toggleDay(day)}
                className={cn(
                  'w-11 h-6 rounded-full transition-colors flex-shrink-0 relative',
                  isOpen ? 'bg-primary' : 'bg-gray-300'
                )}
              >
                <div
                  className={cn(
                    'w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all shadow-sm',
                    isOpen ? 'left-[22px]' : 'left-0.5'
                  )}
                />
              </button>

              {/* Day label */}
              <span
                className={cn(
                  'text-sm font-medium w-24 flex-shrink-0',
                  isOpen ? 'text-heading' : 'text-gray-400'
                )}
              >
                {DAY_LABELS[day]}
              </span>

              {isOpen && hours ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="time"
                    value={hours.open}
                    onChange={(e) => updateDayHours(day, 'open', e.target.value)}
                    className="h-9 rounded-lg border border-gray-200 px-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-primary-light"
                  />
                  <span className="text-body text-sm">to</span>
                  <input
                    type="time"
                    value={hours.close}
                    onChange={(e) => updateDayHours(day, 'close', e.target.value)}
                    className="h-9 rounded-lg border border-gray-200 px-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-primary-light"
                  />
                  <button
                    type="button"
                    onClick={() => copyToAll(day)}
                    className="ml-auto text-xs text-primary hover:text-primary-hover font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
                    title="Copy to all open days"
                  >
                    <Copy className="h-3 w-3" />
                    <span className="hidden sm:inline">Copy to all</span>
                  </button>
                </div>
              ) : (
                <span className="text-sm text-gray-400 italic">Closed</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            const firstOpen = DAYS_OF_WEEK.find(d => formData.hours[d] !== null);
            if (firstOpen) copyToAll(firstOpen);
          }}
        >
          <Copy className="h-3.5 w-3.5 mr-1.5" />
          Same hours all days
        </Button>
      </div>

      {/* Prep Time */}
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-heading block">
            Average Prep Time
          </label>
          <p className="text-xs text-body mt-0.5">
            How long does it take to prepare an order for pickup?
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {PREP_TIME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => updateField('prepTimeEstimate', option.value)}
              className={cn(
                'px-4 py-2 rounded-full text-sm font-medium border transition-all',
                formData.prepTimeEstimate === option.value
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-body border-gray-200 hover:border-primary/40'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="bg-purple-50/50 border border-primary/10 rounded-card p-4 flex items-start gap-3">
          <Clock className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-body">
            Drivers will be dispatched{' '}
            <span className="font-medium text-heading">
              ~{formData.prepTimeEstimate} minutes
            </span>{' '}
            after an order is approved, giving you time to prepare items.
          </p>
        </div>
      </div>
    </div>
  );
}
